import React from 'react'

import CheckIcon from '@mui/icons-material/Check'
import { useSelector } from 'react-redux'
import { UrlProvider } from 'urls'

import { CampaignCountries, CampaignProgress, CampaignStatusIcon, CampaignsFilters } from 'components/campaigns'
import { formatRange } from 'components/input'
import { type ColumnConfig, Table } from 'components/table'
import { INITIAL_CAMPAIGNS_FILTERS } from 'constants/campaigns-filters'
import { useCampaigns } from 'hooks/useCampaigns'
import { usePaginatedTableMeta } from 'hooks/usePaginatedTableMeta'
import { assignableUsers } from 'models/campaign-model'
import type { CampaignListRow } from 'models/campaigns-model'
import { isLoggedIn } from 'selectors/app-selectors'
import { getInitials } from 'utils/strings'

const renderProgress = (field: 'plan' | 'completion') => (campaign: CampaignListRow) => (
  <CampaignProgress value={campaign[field] || 0} />
)

const getRowLink = ({ status, id }: CampaignListRow) =>
  UrlProvider.getUrl(status === 'review' ? 'campaign.edit' : 'campaign', { id })

const config: ColumnConfig<CampaignListRow>[] = [
  { title: 'Title', field: 'title', noBreak: true, headerStyle: { width: '250px' }, style: { width: '250px' } },
  { title: 'Customer', field: 'customer_name', noBreak: true },
  {
    title: 'S',
    align: 'center',
    style: { lineHeight: '15px' },
    render: ({ status }) => <CampaignStatusIcon status={status} />,
  },
  {
    title: 'Countries',
    render: ({ countries }) => <CampaignCountries countries={countries || []} limit={3} />,
    headerStyle: { maxWidth: 80 },
    noBreak: true,
    align: 'center',
  },
  { title: 'Start date', field: 'start_date', sort: 'start_date', noBreak: true },
  { title: 'End date', field: 'end_date', sort: 'end_date', noBreak: true },
  { title: 'Days left', field: 'days_till_end', sort: 'days_till_end', noBreak: true, align: 'right' },
  { title: 'Matched views', field: 'matched_views', sort: 'matched_views', align: 'right', summarize: true },
  {
    title: 'Goal views',
    field: 'total_views',
    sort: 'total_views',
    align: 'right',
    style: { paddingRight: '10px' },
    summarize: true,
  },
  { title: 'Plan', render: renderProgress('plan'), sort: 'plan' },
  { title: 'Completion', render: renderProgress('completion'), sort: 'completion', headerStyle: { maxWidth: 80 } },
  {
    title: 'Payment method',
    render: ({ payment_method }) => getInitials(payment_method),
    align: 'center',
    headerStyle: { maxWidth: 20 },
  },
  {
    title: 'Partner',
    render: ({ is_partner }) => is_partner && <CheckIcon />,
    align: 'center',
  },
  {
    title: 'Assignee',
    render: ({ adops_id }) => {
      if (!adops_id) return ''
      const assignedUser = assignableUsers.find(({ id }: any) => id === adops_id)
      return assignedUser ? getInitials((assignedUser as any).name) : adops_id
    },
    align: 'center',
  },
]

const Campaigns = () => {
  const loggedIn = useSelector(isLoggedIn)
  const { meta, setPage, setRowsPerPage, setSort, setFilters } = usePaginatedTableMeta(
    {
      filters: INITIAL_CAMPAIGNS_FILTERS,
      sortBy: 'start_date',
    },
    'campaigns',
  )
  const { data, isLoading, error } = useCampaigns(meta, loggedIn)

  const handleSort = (property: any) => () => setSort(property)

  // The figure columns are scoped to the selected period, so say which period they describe.
  const period = meta.filters.period
  const title = period ? `Campaigns · ${formatRange(period)}` : 'Campaigns'

  return (
    <div className="campaigns-monitoring">
      <Table
        className="campaigns-monitoring__table"
        config={config}
        count={data?.count}
        data={data?.items}
        error={error ? 'Error occurred when loading campaigns data' : undefined}
        getRowLink={getRowLink}
        hover
        loading={isLoading}
        onChangePage={setPage}
        onChangeRowsPerPage={setRowsPerPage}
        onSort={handleSort}
        page={meta.page}
        rowsPerPage={meta.rowsPerPage}
        sortBy={meta.sortBy}
        sortOrder={meta.sortOrder}
        title={title}
        toolbar={<CampaignsFilters initialValues={meta.filters} setFilters={setFilters} />}
      />
    </div>
  )
}

export default Campaigns
